import type {
  PassType,
  DashboardUIType,
  RevenueBreakdown,
  RevenueCategoryKey,
  MemberSummaryData,
  DashboardInnerTabType,
  SalesTabType,
  MemberTabType,
} from "types/BusinessDashboard";
import {
  SERVICE_TYPE_META,
  formatRefundCancelWon,
} from "constants/salesServiceType";
import { formatNumberToWonCurrency } from "utils/format";

export const businessDashboardTabOptions: {
  id: DashboardUIType;
  label: string;
}[] = [
  { id: "realtime", label: "실시간" },
  { id: "sales", label: "매출" },
  { id: "members", label: "유효회원" },
];

export const businessDashboardInnerTabOptions: {
  id: DashboardInnerTabType;
  label: string;
}[] = [
  { id: "day", label: "오늘" },
  { id: "week", label: "이번 주" },
  { id: "month", label: "이번 달" },
];

export const salesDashboardInnerTabOptions: {
  id: SalesTabType;
  label: string;
}[] = [
  { id: "daily", label: "일일 매출" },
  { id: "cumulative", label: "누적 매출" },
];

export const salesDashboardInnerMembersTabOptions: {
  id: MemberTabType;
  label: string;
}[] = [
  { id: "period", label: "기간제" },
  { id: "count", label: "횟수제" },
];

export const MEMBERS_PASS_TYPE_MAP: {
  [key: string]: {
    passType: PassType;
    apiKey: keyof MemberSummaryData;
    title: string;
    color: string;
    lightColor: string;
    bgColor: string;
  };
} = {
  total: {
    passType: "TOTAL",
    apiKey: "total",
    title: "전체",
    color: "#333333",
    lightColor: "#222222",
    bgColor: "#FFFFFF",
  },
  pt: {
    passType: "PERSONAL_LESSON",
    apiKey: "personalLesson",
    title: "개인",
    color: "#4E23A8",
    lightColor: "#6E3CD6",
    bgColor: "#F6F3FC",
  },
  groupClass: {
    passType: "GROUP_CLASS",
    apiKey: "groupClass",
    title: "그룹",
    color: "#C27500",
    lightColor: "#F0A93C",
    bgColor: "#FEF9F2",
  },
  membership: {
    passType: "MEMBERSHIP",
    apiKey: "membership",
    title: "회원권",
    color: "#003AAC",
    lightColor: "#1E65F0",
    bgColor: "#F3F5FE",
  },
  option: {
    passType: "OPTION",
    apiKey: "option",
    title: "옵션",
    color: "#2E7A9F",
    lightColor: "#3CA5D8",
    bgColor: "#F0F8FB",
  },
};

export const MEMBERS_LIGHT_BREAKDOWN_COLORS: { [key: string]: string } = {
  pt: "#6E3CD6",
  groupClass: "#F0A93C",
  membership: "#1E65F0",
  option: "#3CA5D8",
};

/**
 * 매출 대시보드 분류 순회 순서. **이 6개의 합이 총매출이다.**
 * `refundCancel`은 분류가 아니므로 여기 없다 (각 분류가 이미 환불 차감 순액이라
 * 합계에서 또 빼면 이중 차감이 된다).
 */
export const REVENUE_CATEGORY_ORDER: readonly RevenueCategoryKey[] = [
  "membership",
  "personalLesson",
  "groupClass",
  "option",
  "transferCommission",
  "etc",
];

/**
 * 실시간 매출 카드의 분류별 라벨·색.
 *
 * 예전에는 PassType으로 키를 잡은 REALTIME_PASS_TYPE_MAP을 두고 응답 키를
 * PassType으로 한 번 변환해 조회했는데, 그 맵의 key·lightColor·bgColor와
 * TOTAL 항목은 아무도 읽지 않는 죽은 값이었다. 소비하는 쪽 키(응답 키)로
 * 바로 잡아 변환 단계를 없앴다.
 *
 * 라벨이 SERVICE_TYPE_META와 다른 건 이 화면군이 시설이용을 "회원권",
 * 옵션이용을 "옵션"으로 부르기 때문이다. 라벨 통일은 이번 범위가 아니다.
 */
const REVENUE_CATEGORY_UI: {
  [key in RevenueCategoryKey]: { label: string; color: string };
} = {
  membership: {
    label: "회원권",
    color: SERVICE_TYPE_META.PLACE.colors.dark,
  },
  personalLesson: {
    label: SERVICE_TYPE_META.APPOINTMENT.label,
    color: SERVICE_TYPE_META.APPOINTMENT.colors.dark,
  },
  groupClass: {
    label: SERVICE_TYPE_META.CLASS.label,
    color: SERVICE_TYPE_META.CLASS.colors.dark,
  },
  option: {
    label: "옵션",
    color: SERVICE_TYPE_META.OPTION.colors.dark,
  },
  transferCommission: {
    label: SERVICE_TYPE_META.TRANSFER_COMMISSION.label,
    color: SERVICE_TYPE_META.TRANSFER_COMMISSION.colors.dark,
  },
  etc: {
    label: SERVICE_TYPE_META.ETC.label,
    color: SERVICE_TYPE_META.ETC.colors.dark,
  },
};

export const transformSalesDataForRevenueCard = (
  title: string,
  periodStr: string,
  salesData?: RevenueBreakdown
) => {
  if (!salesData) {
    return {
      title,
      period: periodStr,
      amount: "₩0",
      changeRate: { calculable: false, positive: null, value: null },
      category: "데이터 없음",
      categoryColor: "#6C727F",
      breakdown: [],
      refundCancel: null,
    };
  }

  // 분류 순회는 항상 REVENUE_CATEGORY_ORDER로 한다.
  // `const { total, ...rest }` 식 rest-spread로 순회하면 refundCancel이
  // 7번째 분류 조각으로 자동 편입되어 목록·주력매출 계산이 오염된다.
  const categories = REVENUE_CATEGORY_ORDER.map((key) => {
    const uiInfo = REVENUE_CATEGORY_UI[key];
    const metric = salesData[key];

    return {
      key,
      revenue: metric?.revenue ?? 0,
      name: uiInfo.label,
      amount: formatNumberToWonCurrency(metric?.revenue),
      changeRate: metric?.changeRate,
      color: uiInfo.color,
    };
  });

  // 주력 매출도 6분류 안에서만 고른다. 환불액이 최대여도 주력이 되지 않는다.
  // 전부 0이면 비교가 한 번도 참이 되지 않아 시드(첫 분류)가 그대로 나온다 —
  // 매출이 없는데 "주력 매출: 시설이용"이 뜨므로 그때는 비운다.
  const hasAnyRevenue = categories.some((item) => item.revenue > 0);
  const topCategory = hasAnyRevenue
    ? categories.reduce(
        (max, current) => (current.revenue > max.revenue ? current : max),
        categories[0]
      )
    : null;

  const refundCancelMetric = salesData.refundCancel;

  return {
    title,
    period: periodStr,
    amount: formatNumberToWonCurrency(salesData.total?.revenue),
    changeRate: salesData.total?.changeRate,
    category: topCategory?.name ?? "-",
    categoryColor: topCategory?.color ?? "#6C727F",
    breakdown: categories.map(({ name, amount, changeRate, color }) => ({
      name,
      amount,
      changeRate,
      color,
    })),
    /**
     * 환불·취소는 분류가 아니므로 breakdown 밖에 둔다 (6조각 + 별도 줄, 7조각 아님).
     * 서버가 양수 크기로 주므로 음수 부호는 여기서 붙인다.
     */
    refundCancel: refundCancelMetric
      ? {
          amount: formatRefundCancelWon(refundCancelMetric.revenue),
          changeRate: refundCancelMetric.changeRate,
        }
      : null,
  };
};

/**
 * `title`은 탭 바 라벨(짧게), `cardTitle`은 요약 카드 제목(정식 명칭)이다.
 * 둘을 겸용하면 탭 폭 때문에 축약한 라벨이 카드 제목까지 바꿔 버린다.
 *
 * 색은 두지 않는다 — 예전에 있던 color·bgColor는 아무도 읽지 않는 죽은 값이었고,
 * bgColor에는 CSS 값이 아닌 테일윈드 클래스 문자열("bg-[#F4F5F6]")까지 섞여 있었다.
 * 분류 색이 필요하면 SERVICE_TYPE_META를 쓴다.
 */
export const SALES_PASS_TYPE_MAP: {
  [key: string]: {
    passType: PassType;
    title: string;
    cardTitle: string;
  };
} = {
  total: { passType: "TOTAL", title: "전체", cardTitle: "총 매출" },
  pt: {
    passType: "PERSONAL_LESSON",
    title: "개인",
    cardTitle: SERVICE_TYPE_META.APPOINTMENT.label,
  },
  groupClass: {
    passType: "GROUP_CLASS",
    title: "그룹",
    cardTitle: SERVICE_TYPE_META.CLASS.label,
  },
  membership: { passType: "MEMBERSHIP", title: "회원권", cardTitle: "회원권" },
  option: {
    passType: "OPTION",
    title: "옵션",
    cardTitle: SERVICE_TYPE_META.OPTION.label,
  },
  // 탭 라벨은 "양도"로 축약한다. 7탭이 되면서 탭 바 가로 폭이 빠듯해지고,
  // 기존 탭도 개인·그룹으로 축약된 관행과 맞춘다.
  transferCommission: {
    passType: "TRANSFER_COMMISSION",
    title: "양도",
    cardTitle: SERVICE_TYPE_META.TRANSFER_COMMISSION.label,
  },
  etc: {
    passType: "ETC",
    title: "기타",
    cardTitle: SERVICE_TYPE_META.ETC.label,
  },
};

/**
 * 단가를 표시하지 않는 분류.
 *
 * 집계 테이블의 단가 축에 양도 수수료 행이 없어 TRANSFER_COMMISSION은 TOTAL과
 * 같은 쿼리를 탄다 — 매출 금액은 해당 분류만, 단가는 가맹점 전체 값이 온다.
 * ETC는 단가만 service_type='ETC'로 필터돼, 매출 쪽 기타(총매출에서 5종을 뺀
 * 잔여)와 분모가 다르다. 각주로도 오독 위험이 커서 숨긴다.
 *
 * 요약 카드·단가 차트·일자별 표가 같이 읽어야 하므로 여기 한 곳에 둔다.
 */
export const UNIT_PRICE_UNAVAILABLE_PASS_TYPES: PassType[] = [
  "TRANSFER_COMMISSION",
  "ETC",
];

/**
 * URL의 `tab` 파라미터는 사용자가 임의로 넣을 수 있다.
 * 맵에 없는 값이 오면 총매출로 폴백한다 (예전에는 undefined 접근으로 크래시했다).
 */
export const getSalesApiPassType = (
  activeTab: keyof typeof SALES_PASS_TYPE_MAP
): PassType => SALES_PASS_TYPE_MAP[activeTab]?.passType ?? "TOTAL";

export const salesDashboardTabOptions = Object.entries(SALES_PASS_TYPE_MAP).map(
  ([id, { title }]) => ({ id, title })
);

export const membersDashboardTabOptions = Object.entries(
  MEMBERS_PASS_TYPE_MAP
).map(([id, { title }]) => ({
  id,
  title,
}));
